/**
 * Browser-based terminal using xterm.js over WebSocket.
 * Provides a full PTY shell for auth flows, debugging, and container access.
 */

import { useEffect, useRef, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { useTerminal } from './useTerminal';
import type { TerminalStatus } from './useTerminal';

interface WebTerminalProps {
  /** Command to auto-type when terminal connects (e.g., "claude auth login") */
  initialCommand?: string;
  /** Callback when terminal status changes */
  onStatusChange?: (status: TerminalStatus) => void;
  /** Callback when the process exits successfully (code 0) — used for auto-close on auth */
  onProcessSuccess?: () => void;
  /** Additional CSS classes */
  className?: string;
}

export function WebTerminal({
  initialCommand,
  onStatusChange,
  onProcessSuccess,
  className,
}: WebTerminalProps): React.ReactElement {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const initialCommandSent = useRef(false);

  const handleOutput = useCallback((data: string): void => {
    xtermRef.current?.write(data);
  }, []);

  const handleExit = useCallback(
    (code: number): void => {
      if (code === 0) {
        xtermRef.current?.writeln('\r\n\x1b[32m[Auth complete — importing credentials...]\x1b[0m');
        onProcessSuccess?.();
      } else {
        xtermRef.current?.writeln(`\r\n\x1b[33m[Process exited with code ${code}]\x1b[0m`);
      }
    },
    [onProcessSuccess]
  );

  const { status, connect, disconnect, sendInput, sendResize } = useTerminal({
    onOutput: handleOutput,
    onExit: handleExit,
    onStatusChange,
  });

  // Initialize xterm.js
  useEffect(() => {
    if (!terminalRef.current || xtermRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      fontSize: 13,
      lineHeight: 1.2,
      theme: {
        background: '#1a1a2e',
        foreground: '#e0e0e0',
        cursor: '#5588ff',
        selectionBackground: '#3355aa55',
        black: '#1a1a2e',
        red: '#ff5555',
        green: '#50fa7b',
        yellow: '#f1fa8c',
        blue: '#5588ff',
        magenta: '#bd93f9',
        cyan: '#8be9fd',
        white: '#e0e0e0',
        brightBlack: '#6272a4',
        brightRed: '#ff6e6e',
        brightGreen: '#69ff94',
        brightYellow: '#ffffa5',
        brightBlue: '#6699ff',
        brightMagenta: '#d6acff',
        brightCyan: '#a4ffff',
        brightWhite: '#ffffff',
      },
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);

    term.open(terminalRef.current);
    fitAddon.fit();

    // Send keyboard input to the WebSocket
    term.onData((data: string) => {
      sendInput(data);
    });

    // Handle resize
    term.onResize(({ cols, rows }) => {
      sendResize(cols, rows);
    });

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    // Auto-connect
    connect();

    // Resize observer
    const resizeObserver = new ResizeObserver((): void => {
      fitAddon.fit();
    });
    resizeObserver.observe(terminalRef.current);

    return (): void => {
      resizeObserver.disconnect();
      disconnect();
      term.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
    };
  }, []); // intentional: init once on mount

  // Auto-type initial command after connection
  useEffect(() => {
    if (status === 'connected' && initialCommand && !initialCommandSent.current) {
      initialCommandSent.current = true;
      // Small delay to let the shell initialize
      setTimeout(() => {
        sendInput(initialCommand + '\n');
      }, 500);
    }
  }, [status, initialCommand, sendInput]);

  return (
    <div className={`flex flex-col ${className ?? ''}`}>
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#1a1a2e] border-b border-border rounded-t-md">
        <div className="flex items-center gap-2">
          <div
            className={`h-2 w-2 rounded-full ${
              status === 'connected'
                ? 'bg-success'
                : status === 'connecting'
                  ? 'bg-warning animate-pulse'
                  : status === 'error'
                    ? 'bg-error'
                    : 'bg-text-tertiary'
            }`}
          />
          <span className="text-xs text-text-secondary">
            {status === 'connected'
              ? 'Terminal'
              : status === 'connecting'
                ? 'Connecting...'
                : status === 'error'
                  ? 'Connection error'
                  : 'Disconnected'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {status === 'disconnected' && (
            <button
              onClick={connect}
              className="text-xs text-text-secondary hover:text-text-primary px-2 py-0.5"
            >
              Reconnect
            </button>
          )}
          {status === 'connected' && (
            <button
              onClick={disconnect}
              className="text-xs text-text-secondary hover:text-text-primary px-2 py-0.5"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>
      <div ref={terminalRef} className="flex-1 min-h-[300px] bg-[#1a1a2e] rounded-b-md" />
    </div>
  );
}
